import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Search, Loader2, Eye, Trash2, Mail, Phone, Building2, MessageSquare, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  received:       { label: 'Reçue',         className: 'bg-blue-100 text-blue-800 border-blue-200' },
  in_negotiation: { label: 'En négociation', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  accepted:       { label: 'Acceptée',      className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected:       { label: 'Rejetée',       className: 'bg-rose-100 text-rose-800 border-rose-200' },
};

export default function SiteRequestsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ received: 0, in_negotiation: 0, accepted: 0, rejected: 0 });
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await api.get('/admin/site-requests', { params });
      setItems(data.items);
      setTotal(data.total);
      setCounts(data.counts || counts);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { const t = setTimeout(() => setSearch(searchInput), 300); return () => clearTimeout(t); }, [searchInput]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const { data } = await api.get(`/admin/site-requests/${id}`);
      setDetail(data);
      setEditStatus(data.status);
      setEditNotes(data.admin_notes || '');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Erreur de chargement');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const saveChanges = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/site-requests/${detail.id}`, {
        status: editStatus,
        admin_notes: editNotes,
      });
      setDetail(data);
      toast.success('Demande mise à jour');
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/site-requests/${deleteTarget.id}`);
      toast.success(`Demande ${deleteTarget.request_number} supprimée`);
      setDeleteTarget(null);
      setDetail(null);
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Erreur de suppression');
    }
  };

  const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '-'; } };

  const StatusBadge = ({ status }) => {
    const meta = STATUS_META[status] || { label: status, className: 'bg-slate-100 text-slate-700 border-slate-200' };
    return <Badge variant="outline" className={`${meta.className} font-medium`} data-testid={`status-badge-${status}`}>{meta.label}</Badge>;
  };

  const filterTabs = [
    { key: 'all', label: 'Toutes', count: total },
    { key: 'received', label: 'Reçues', count: counts.received },
    { key: 'in_negotiation', label: 'En négociation', count: counts.in_negotiation },
    { key: 'accepted', label: 'Acceptées', count: counts.accepted },
    { key: 'rejected', label: 'Rejetées', count: counts.rejected },
  ];

  return (
    <div className="space-y-5" data-testid="site-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Demandes site</h1>
          <p className="text-sm text-slate-500">{total} demande{total !== 1 ? 's' : ''} au total</p>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2">
        {filterTabs.map(t => (
          <button
            key={t.key}
            onClick={() => setStatusFilter(t.key)}
            data-testid={`filter-${t.key}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              statusFilter === t.key
                ? 'bg-[#0A3D73] border-[#0A3D73] text-white'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {t.label}
            <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${statusFilter === t.key ? 'bg-white/20' : 'bg-slate-100 text-slate-600'}`}>{t.count ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-slate-100">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="N°, client, email, société..."
              className="pl-10 h-9"
              data-testid="site-requests-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Inbox className="w-10 h-10 mb-2 text-slate-300" />
            <p className="text-sm">Aucune demande</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N°</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="hidden md:table-cell">Société</TableHead>
                <TableHead className="hidden lg:table-cell">Contact</TableHead>
                <TableHead className="text-center">Produits</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="hidden md:table-cell">Reçue le</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(r => (
                <TableRow key={r.id} data-testid={`site-request-row-${r.request_number}`}>
                  <TableCell className="font-mono text-sm font-medium text-[#0A3D73]">{r.request_number}</TableCell>
                  <TableCell className="font-medium">{r.customer_name}</TableCell>
                  <TableCell className="hidden md:table-cell text-slate-600">{r.company || '-'}</TableCell>
                  <TableCell className="hidden lg:table-cell text-slate-600 text-sm">
                    <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.customer_email}</div>
                    {r.customer_phone && <div className="flex items-center gap-1 text-slate-500"><Phone className="w-3 h-3" />{r.customer_phone}</div>}
                  </TableCell>
                  <TableCell className="text-center text-slate-600">{r.items?.length || 0}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="hidden md:table-cell text-slate-500 text-sm">{fmtDate(r.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openDetail(r.id)} data-testid={`view-request-${r.request_number}`}>
                      <Eye className="w-4 h-4 mr-1" /> Voir
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="site-request-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono text-[#0A3D73]">{detail?.request_number || '...'}</DialogTitle>
            <DialogDescription>Détail de la demande site</DialogDescription>
          </DialogHeader>

          {detailLoading || !detail?.customer_name ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
            </div>
          ) : (
            <div className="space-y-5">
              {/* Customer info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-slate-50 rounded-lg">
                <div>
                  <div className="text-xs text-slate-500 mb-0.5">Client</div>
                  <div className="font-medium">{detail.customer_name}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-0.5 flex items-center gap-1"><Building2 className="w-3 h-3" />Société</div>
                  <div className="font-medium">{detail.company || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-0.5 flex items-center gap-1"><Mail className="w-3 h-3" />Email</div>
                  <a href={`mailto:${detail.customer_email}`} className="font-medium text-[#0A3D73] hover:underline">{detail.customer_email}</a>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-0.5 flex items-center gap-1"><Phone className="w-3 h-3" />Téléphone</div>
                  <div className="font-medium">{detail.customer_phone || '—'}</div>
                </div>
              </div>

              {/* Message */}
              {detail.message && (
                <div>
                  <Label className="flex items-center gap-1 mb-1.5"><MessageSquare className="w-3.5 h-3.5" />Message du client</Label>
                  <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded text-sm text-slate-700 whitespace-pre-wrap">{detail.message}</div>
                </div>
              )}

              {/* Items */}
              <div>
                <Label className="mb-1.5">Produits demandés ({detail.items?.length || 0})</Label>
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Référence</TableHead>
                        <TableHead>Désignation</TableHead>
                        <TableHead className="text-center w-20">Qté</TableHead>
                        <TableHead>Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(detail.items || []).length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center text-slate-500 italic py-4">Aucun produit listé</TableCell></TableRow>
                      ) : detail.items.map((it, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-sm">{it.reference || '—'}</TableCell>
                          <TableCell>{it.name}</TableCell>
                          <TableCell className="text-center">{it.quantity}</TableCell>
                          <TableCell className="text-slate-500 text-sm">{it.note || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Admin controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200">
                <div>
                  <Label htmlFor="status-select" className="mb-1.5">Statut</Label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger id="status-select" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="received">Reçue</SelectItem>
                      <SelectItem value="in_negotiation">En négociation</SelectItem>
                      <SelectItem value="accepted">Acceptée</SelectItem>
                      <SelectItem value="rejected">Rejetée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end text-sm text-slate-500">
                  <div>
                    <div>Reçue le {fmtDate(detail.created_at)}</div>
                    {detail.updated_at && detail.updated_at !== detail.created_at && <div>Modifiée le {fmtDate(detail.updated_at)}</div>}
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="admin-notes" className="mb-1.5">Notes internes</Label>
                <Textarea
                  id="admin-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  placeholder="Suivi commercial, prochaines actions..."
                  data-testid="admin-notes-input"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-row justify-between items-center gap-2">
            <div>
              {(user?.role === 'admin' || user?.role === 'super_admin') && detail?.customer_name && (
                <Button variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => setDeleteTarget(detail)} data-testid="delete-request-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDetail(null)}>Fermer</Button>
              <Button
                onClick={saveChanges}
                disabled={saving || !detail?.customer_name}
                className="bg-[#0A3D73] hover:bg-[#082E56] text-white"
                data-testid="save-request-btn"
              >
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Enregistrer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette demande ?</AlertDialogTitle>
            <AlertDialogDescription>
              La demande <span className="font-mono font-medium">{deleteTarget?.request_number}</span> sera supprimée définitivement. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-request">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
